import { useState, useEffect, useContext, useCallback } from "react";
import { FaPlus, FaEdit, FaTrash } from "react-icons/fa";
import { XMarkIcon } from "@heroicons/react/24/solid";
import axios from "axios";
import NoteForm from "./NotePage";
import { API_URL } from "../../Constants";
import { AuthContext } from "../context/AuthContext";

export default function Note() {
  const { user } = useContext(AuthContext);
  const [notes, setNotes] = useState([]);
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [noteToEdit, setNoteToEdit] = useState(null);
  const [editNoteText, setEditNoteText] = useState("");
  const [editNoteType, setEditNoteType] = useState("");
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);

  const companyId = user?.accountType === "COMPANY" ? user.id : user?.companyId;

  const fetchNotes = useCallback(
    async (page = pageNumber, size = pageSize) => {
      const token = localStorage.getItem("token");
      try {
        let response;
        if (user?.accountType === "COMPANY") {
          response = await axios.get(
            `${API_URL}/api/notes/company/${companyId}?pageNumber=${page}&pageSize=${size}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
        } else {
          response = await axios.get(
            `${API_URL}/api/notes/createdBy/${user?.id}?pageNumber=${page}&pageSize=${size}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
        }

        const { notes, totalPages } = response.data;
        setNotes(Array.isArray(notes) ? notes : []);
        setTotalPages(totalPages || 0);
      } catch (error) {
        console.error("Error fetching notes:", error);
        setNotes([]);
        setTotalPages(0);
      }
    },
    [pageNumber, pageSize, companyId, user?.accountType, user?.id]
  );

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const handleDeleteClick = (note) => {
    setNoteToDelete(note);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    const token = localStorage.getItem("token");
    try {
      await axios.delete(`${API_URL}/api/notes/${noteToDelete.noteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotes(notes.filter((n) => n.noteId !== noteToDelete.noteId));
      setShowDeleteConfirm(false);
      setNoteToDelete(null);
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  const handleEditClick = (note) => {
    setNoteToEdit(note);
    setEditNoteText(note.note || "");
    setEditNoteType(note.type || "");
  };

  const handleEditSave = async () => {
    const token = localStorage.getItem("token");
    try {
      await axios.put(
        `${API_URL}/api/notes/${noteToEdit.noteId}`,
        { ...noteToEdit, note: editNoteText, type: editNoteType },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const updatedNotes = notes.map((n) =>
        n.noteId === noteToEdit.noteId
          ? { ...n, note: editNoteText, type: editNoteType }
          : n
      );
      setNotes(updatedNotes);
      setNoteToEdit(null);
    } catch (err) {
      console.error("Edit error:", err);
    }
  };

  useEffect(() => {
    const handleNotesCreated = () => {
      console.log("Note created event received");
      fetchNotes();
    };

    window.addEventListener("noteCreated", handleNotesCreated);

    return () => {
      window.removeEventListener("noteCreated", handleNotesCreated);
    };
  }, [fetchNotes]);

  return (
    <>
      <div className="p-2 bg-gray-100 dark:bg-gray-900 p-2 rounded-lg shadow-lg">
        {/* ---------- top action bar ---------- */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-2 mb-4">
          <h1 className="text-xl font-extrabold tracking-tight text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <span className="text-indigo-600 dark:text-indigo-400">📝</span>{" "}
            Notes
          </h1>

          <button
            onClick={() => setShowNoteForm(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-indigo-700 active:scale-95 transition"
          >
            <FaPlus className="shrink-0" /> Add Note
          </button>
        </div>

        {/* ---------- data grid ---------- */}
        <div className="overflow-x-auto rounded-xl shadow ring-1 ring-slate-200 dark:ring-slate-700">
          <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700 text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 sticky top-0 z-10">
              <tr>
                <th className="px-2 py-2 font-semibold text-left w-16">
                  S No.
                </th>
                <th className="px-2 py-2 font-semibold text-left">Note</th>
                <th className="px-2 py-2 font-semibold text-left w-40">Type</th>
                {user?.accountType === "COMPANY" && (
                <th className="px-2 py-2 font-semibold text-left w-40">Created By</th>
                )}
                <th className="px-2 py-2 font-semibold text-center w-28">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {notes.length > 0 ? (
                notes.map((note, i) => (
                  <tr
                    key={note.noteId}
                    className="odd:bg-white even:bg-slate-50/60 dark:odd:bg-slate-900 dark:even:bg-slate-800/60 hover:bg-indigo-50 dark:hover:bg-slate-700 transition"
                  >
                    <td className="px-2 py-2 text-center">{i + 1}</td>
                    <td className="px-2 py-2">{note.note || "—"}</td>
                    <td className="px-2 py-2">{note.type}</td>
                    {user?.accountType === "COMPANY" && (
                    <td className="px-2 py-2">{note.createdByName}</td>
                    )}
                    <td className="px-2 py-2">
                      <div className="flex justify-center gap-4">
                        <button
                          onClick={() => handleEditClick(note)}
                          className="text-sky-600 hover:text-sky-800 active:scale-90 transition"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(note)}
                          className="text-rose-600 hover:text-rose-800 active:scale-90 transition"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="4"
                    className="px-4 py-6 text-center text-slate-500"
                  >
                    No notes found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ---------- paginator ---------- */}
        {totalPages > 0 && (
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            {/* page-size selector - now appears first on mobile */}
            <div className="order-1 sm:order-none">
              <label className="flex items-center gap-2 text-sm">
                <span className="text-slate-600 dark:text-slate-300">
                  Items per page:
                </span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPageNumber(0);
                  }}
                  className="rounded border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 focus:ring-indigo-500"
                >
                  {[5, 10, 20, 50].map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {/* nav buttons - now wraps on small screens */}
            <div className="flex flex-wrap justify-center gap-2 sm:gap-3 order-2 sm:order-none">
              <button
                onClick={() => setPageNumber(0)}
                disabled={pageNumber === 0}
                className="rounded bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 px-3 py-1 text-sm font-medium text-slate-800 dark:text-slate-100 disabled:opacity-50 disabled:pointer-events-none min-w-[60px]"
                title="First Page"
              >
                First
              </button>

              <button
                onClick={() => setPageNumber((p) => Math.max(p - 1, 0))}
                disabled={pageNumber === 0}
                className="rounded bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 px-4 py-1 text-sm font-medium text-slate-800 dark:text-slate-100 disabled:opacity-50 disabled:pointer-events-none min-w-[60px]"
              >
                Prev
              </button>

              <span className="flex items-center text-sm px-2 min-w-[120px] justify-center">
                Page {pageNumber + 1} of {totalPages}
              </span>

              <button
                onClick={() =>
                  setPageNumber((p) => Math.min(p + 1, totalPages - 1))
                }
                disabled={pageNumber >= totalPages - 1}
                className="rounded bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 px-4 py-1 text-sm font-medium text-slate-800 dark:text-slate-100 disabled:opacity-50 disabled:pointer-events-none min-w-[60px]"
              >
                Next
              </button>

              <button
                onClick={() => setPageNumber(totalPages - 1)}
                disabled={pageNumber >= totalPages - 1}
                className="rounded bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 px-3 py-1 text-sm font-medium text-slate-800 dark:text-slate-100 disabled:opacity-50 disabled:pointer-events-none min-w-[60px]"
                title="Last Page"
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Note Modal */}
      {showNoteForm && (
        <ModalWrapper onClose={() => setShowNoteForm(false)}>
          <NoteForm
            onClose={() => setShowNoteForm(false)}
            onSave={fetchNotes}
            user={user}
            companyId={companyId}
          />
        </ModalWrapper>
      )}

      {/* Edit Note Modal */}
      {noteToEdit && (
        <ModalWrapper onClose={() => setNoteToEdit(null)}>
          <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">
            Edit Note
          </h2>
          <label className="block mb-2">Note</label>
          <textarea
            value={editNoteText}
            onChange={(e) => setEditNoteText(e.target.value)}
            className="w-full p-2 border rounded dark:bg-gray-700 dark:text-white"
            rows={4}
          />
          <label className="block mb-2">Type</label>
          <input
            value={editNoteType}
            onChange={(e) => setEditNoteType(e.target.value)}
            className="w-full p-2 border rounded dark:bg-gray-700 dark:text-white"
            rows={4}
          />
          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={() => setNoteToEdit(null)}
              className="bg-gray-400 text-white px-4 py-2 rounded"
            >
              Cancel
            </button>
            <button
              onClick={handleEditSave}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
            >
              Save
            </button>
          </div>
        </ModalWrapper>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <ModalWrapper onClose={() => setShowDeleteConfirm(false)}>
          <h2 className="text-lg font-semibold mb-2 text-gray-800 dark:text-white">
            Confirm Delete
          </h2>
          <p className="mb-4 text-gray-600 dark:text-gray-300">
            Are you sure you want to delete this note?
          </p>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setShowDeleteConfirm(false)}
              className="bg-gray-500 text-white px-4 py-2 rounded"
            >
              Cancel
            </button>
            <button
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
            >
              Delete
            </button>
          </div>
        </ModalWrapper>
      )}
    </>
  );
}

function ModalWrapper({ children, onClose }) {
  return (
    <div className="fixed inset-0 flex justify-center items-center z-50 px-4">
      <div
        className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg w-full max-w-lg z-50">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
        {children}
      </div>
    </div>
  );
}
